import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import ToggleSwitch from '../../../../../shared/components/ToggleSwitch';
import Image from '../../../../../shared/components/Image';
import { testIds } from '../../../../../core/testIds';
import SettingsItem from '../SettingsItem';

const LS_KEY = 'lobby_continuePlaying';

const ContinuePlayingSettingItem: FC = () => {
  const [enabled, setEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem(LS_KEY);
    return saved !== null ? saved === 'true' : true;
  });

  useEffect(() => {
    // Initial check
    const saved = localStorage.getItem(LS_KEY);
    if (saved !== null) {
      setEnabled(saved === 'true');
    }

    // Listen for changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LS_KEY && e.newValue !== null) {
        setEnabled(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Update localStorage and dispatch event when enabled changes
  useEffect(() => {
    localStorage.setItem(LS_KEY, String(enabled));
    // Dispatch custom event for same-tab communication
    window.dispatchEvent(new CustomEvent('continuePlayingChanged', { detail: { enabled } }));
  }, [enabled]);

  const handleToggle = useCallback(() => {
    setEnabled(!enabled);
  }, [enabled]);

  return (
    <SettingsItem
      labelKey="generic_continue_playing"
      descriptionKey="continue_playing_tab.continue_playing_description"
      icon={<Image src={'/ui/continuePlaying'} manualAffix=".svg" alt="continuePlaying" />}
      labelTestId={testIds.settings.toggleLabel('continue_playing')}
      descriptionTestId={testIds.settings.toggleDescription('continue_playing')}
    >
      <ToggleSwitch
        checked={enabled}
        onChange={handleToggle}
        dataTestId={testIds.settings.toggle('continue_playing', enabled ? 'on' : 'off')}
      />
    </SettingsItem>
  );
};

export default ContinuePlayingSettingItem;
